import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Inject, Input, NO_ERRORS_SCHEMA, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {environment} from "../../../../environments/environment";
import { Swiper } from 'swiper';
import { SwiperOptions } from 'swiper/types';
import { Navigation, Pagination } from 'swiper/modules';
import {Subscription} from "rxjs";
import {MODE, NearDatesWithPrice, BrandList} from '../../../models';
import {EventBusService} from '../../../services/event-bus.service';
import {WindowRefService} from "../../../services/window-ref.service";
import {NearDatesWithPriceService} from '../../../services/near-dates-with-price.service';
import {TemplateReplacePipe} from "../../pipes/trmplate-replace/template-replace.pipe";
import {SearchService} from "../../../services/search/search.service";
import {HotelToursSearchService} from "../../../services/search/hotel-tours-search.service";
import {HOTEL_TOURS_PAGE_CONFIG, HotelToursPageConfig} from "../../../config/hotel-tours-list-page.config";
import { TourLoaderComponent } from "../tour-loader/tour-loader.component";

@Component({
  selector: 'app-tours-not-found',
  templateUrl: environment.mobile ? './tours-not-found.component.mobile.html' : './tours-not-found.component.html',
  styleUrls: environment.mobile ? ['./tours-not-found.component.mobile.scss'] : ['./tours-not-found.component.scss'],
  standalone: true,
  imports: [CommonModule, TemplateReplacePipe, TourLoaderComponent],
  schemas: [NO_ERRORS_SCHEMA],
})
export class ToursNotFoundComponent implements OnInit, AfterViewInit, OnDestroy {
  private static readonly LOADING_TIMEOUT_MS = 10000;
  @Input() type: string = '';
  @ViewChild('swiperContainer', { static: false }) swiperContainer?: ElementRef<HTMLElement>;
  readonly mobileSkeletonSlides = Array.from({ length: 6 }, (_, index) => index);

  messages: any = {};
  mobileMessages: any = {};
  titleDesktopMapping = {'br': ''};
  titleMobileMapping = {'br': '<br>'};

  swiperSub: Subscription;
  swiperConfig: SwiperOptions = {
    observer: true,
    direction: 'horizontal',
    spaceBetween: 7,
    slidesPerView: 2.1,
    centeredSlides: false,
    pagination: { clickable: true },
    navigation: true,
    breakpoints: {
      320: {
        slidesPerView: 2.3,
      },
      375: {
        slidesPerView: 2.8,
      },
      425: {
        slidesPerView: 3.2,
      },
      450: {
        slidesPerView: 3.5,
      },
      1024: {
        slidesPerView: 4,
      },
    },
  };

  dateFrom: string | null = '';
  datesArray = [];
  loading: boolean = true;
  showEmptyStateContent = false;
  private swiperInstance: Swiper | null = null;
  private loadingTimeoutId: ReturnType<typeof setTimeout> | null = null;
  brandId: number = 0;
  protected readonly BrandList = BrandList;

  constructor(
    @Inject(HOTEL_TOURS_PAGE_CONFIG) private hotelToursPageConfig: HotelToursPageConfig,
    private searchService: SearchService,
    private hotelToursSearchService: HotelToursSearchService,
    private windowRef: WindowRefService,
    private nearDatesWithPriceService: NearDatesWithPriceService,
    private cdRef: ChangeDetectorRef,
    private eventBusService: EventBusService,
  ) {
    this.messages = this.hotelToursPageConfig.translates.toursNotFound;
    this.mobileMessages = this.hotelToursPageConfig.translates.toursNotFoundMobile;
    this.brandId = this.hotelToursPageConfig.brandId;
  }

  ngAfterViewInit() {
    this.initSwiperIfNeeded();
  }

  private removeAvailableDatesListener?: () => void;
  private searchAnalyticsContext: any = null;
  private analyticsDestroyed = false;
  private blockShownSent = false;

  private get analyticsContext() {
    return this.searchAnalyticsContext || this.getSearchService().getAnalyticsContext();
  }

  private trackBlockShown(): void {
    const current = this.getSearchService().getAnalyticsContext();
    const context = this.analyticsContext;
    if (this.analyticsDestroyed || this.blockShownSent || !context
        || current?.search_attempt_id !== context.search_attempt_id
        || current?.search_phase !== context.search_phase) { return; }
    this.blockShownSent = true;
    this.getSearchService().trackAlternativeDates(context, this.datesArray.flat().length);
  }

  ngOnDestroy() {
    this.analyticsDestroyed = true;
    this.removeAvailableDatesListener?.();
    this.clearLoadingTimeout();
    this.destroySwiper();
  }

  ngOnInit() {
    this.searchAnalyticsContext = this.getSearchService().getAnalyticsContext();
    if (this.hotelToursPageConfig.mode === MODE.MODE_TOUR_LIST) {
      this.finishLoading();
      this.trackBlockShown();
      return;
    }
    this.showEmptyStateContent = this.type === 'find-tours';
    this.startLoadingTimeout();
    const searchService = this.getSearchService();
    const lastSearchParams = searchService.getLastSearchParams();
    // The form price calendar supplies flight dates only, never accommodation dates.
    if (this.hotelToursPageConfig.mode === MODE.MODE_SEARCH && this.searchService.getLastSearchParams()?.onlyHotels) {
      this.finishLoading();
      this.trackBlockShown();
      return;
    }
    const searchFormValues = this.hotelToursPageConfig.searchFormValues as any;

    const dateFrom = this.resolveDateValue(lastSearchParams?.dateFrom, searchFormValues?.dateFrom);
    const dateTo = this.resolveDateValue(lastSearchParams?.dateTo, searchFormValues?.dateTo, dateFrom);
    const departCityId = this.resolveNumberValue(
      lastSearchParams?.departCityId,
      (lastSearchParams as any)?.departCity,
      searchFormValues?.departCityId,
      searchFormValues?.departCity,
    );
    const countryId = this.resolveNumberValue(
      lastSearchParams?.countryId,
      (lastSearchParams as any)?.country,
      searchFormValues?.countryId,
      searchFormValues?.country,
    );
    const nightsFrom = this.resolveNumberValue(lastSearchParams?.nightsFrom, searchFormValues?.nightsFrom);
    const nightsTo = this.resolveNumberValue(lastSearchParams?.nightsTo, searchFormValues?.nightsTo, nightsFrom);

    if (!dateFrom || !dateTo) {
      this.finishLoading();
      return;
    }

    const resultDate = this.getDateFrom(dateFrom, dateTo);

    if (this.hotelToursPageConfig.mode === MODE.MODE_SEARCH) {
      this.loadSearchFormAvailableDates(departCityId, countryId, dateFrom, dateTo);
      return;
    }

    if (this.hotelToursPageConfig.mode !== MODE.MODE_TOUR_LIST) {
      if (departCityId == null || countryId == null) {
        this.finishLoading();
        return;
      }

      this.nearDatesWithPriceService
        .getNearDatesWithPrice(resultDate, departCityId, countryId)
        .subscribe({
          next: (nearDatesWithPrice: NearDatesWithPrice[]) => {
            this.generateDateArrayFromAvailableDates(
              nearDatesWithPrice.map(({ date }) => date), dateFrom, dateTo
            );

            this.finishLoading();
            this.triggerEventRenderHotOffers();
            this.trackBlockShown();
          },
          error: () => {
            this.finishLoading();
          }
        });
    }
  }

  private loadSearchFormAvailableDates(departCityId: number, countryId: number, dateFrom: string, dateTo: string): void {
    const win = this.windowRef.nativeWindow;
    const apply = (data: {departCityId: number; countryId: number; dates: string[]; requestParams: any}) => {
      if (!data || data.departCityId !== departCityId || data.countryId !== countryId
          || !this.matchesCalendarRequest(data.requestParams)) {
        return false;
      }
      this.generateDateArrayFromAvailableDates(data.dates, dateFrom, dateTo);
      this.removeAvailableDatesListener?.();
      this.finishLoading();
      this.trackBlockShown();
      return true;
    };
    if (apply(win.findFormAvailableDates)) {
      return;
    }
    const listener = (event: CustomEvent) => apply(event.detail);
    win.addEventListener('find-form:available-dates-loaded', listener);
    this.removeAvailableDatesListener = () => win.removeEventListener('find-form:available-dates-loaded', listener);
  }

  private matchesCalendarRequest(request: any): boolean {
    const params = this.searchService.getLastSearchParams();
    if (!request || !params) { return false; }
    const csv = (value: any) => (Array.isArray(value) ? value : String(value || '').split(','))
      .map(v => String(v).trim()).filter(Boolean).sort().join(',');
    const firstHotel = (Array.isArray(params.hotels) ? params.hotels : String(params.hotels || '').split(','))[0];
    return Number(request.nightsFrom) === Number(params.nightsFrom)
      && Number(request.nightsTo) === Number(params.nightsTo)
      && String(request.selected_hotel || '') === String(firstHotel || '')
      && csv(request.region_ids) === csv(params.regions);
  }

  getSearchService() {
    return this.hotelToursPageConfig.mode === MODE.MODE_TOUR_LIST ? this.hotelToursSearchService : this.searchService;
  }

  getDateFrom(dateFrom: string, dateTo: string) {
    // Преобразуем строки дат в объекты Date
    const [fromYear, fromMonth, fromDay] = dateFrom.split('-').map(Number);
    const [toYear, toMonth, toDay] = dateTo.split('-').map(Number);

    const fromDate = new Date(fromYear, fromMonth - 1, fromDay);
    const toDate = new Date(toYear, toMonth - 1, toDay);

    // Если dateFrom равно dateTo, возвращаем dateFrom
    if (fromDate.getTime() === toDate.getTime()) {
      return dateFrom;
    }

    // Добавляем 2 дня к dateFrom
    fromDate.setDate(fromDate.getDate() + (environment.mobile ? 2 : 3));

    return this.formatDate(fromDate);
  }

  generateDateArrayFromDatesWithPrice(datesWithPrice: { date: string }[]): void {
    this.datesArray = [];

    if (!datesWithPrice || datesWithPrice.length === 0) {
      return;
    }

    datesWithPrice.forEach((dateObj) => {
      const [day, month, year] = dateObj.date.split('.').map(Number);
      let date = new Date(year, month - 1, day); // Преобразуем строку даты в объект Date

      // Форматируем дату в нужный формат
      const formattedDate = date.toLocaleDateString('ru-RU', {
        day: 'numeric',
        month: 'short'
      });

      const isoDate = this.formatDate(date, 'd.m.Y'); // Форматируем дату в ISO формате d.m.Y

      // Добавляем форматированные данные в массив в нужном формате
      this.datesArray.push([
        {
          label: formattedDate,
          date: isoDate
        }
      ]);
    });
  }

  generateDateArrayFromAvailableDates(availableDates: string[], dateFrom: string, dateTo: string): void {
    const filteredDates = this.pickNearestAvailableDates(availableDates, dateFrom, dateTo)
      .map((date) => this.toDottedDate(date));

    this.generateDateArrayFromDatesWithPrice(filteredDates.map((date) => ({ date })));
  }

  formatDate(date: Date, format = 'Y-m-d'): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Месяцы с 0, поэтому +1
    const day = date.getDate().toString().padStart(2, '0');

    if (format === 'Y-m-d') {
      return `${year}-${month}-${day}`;
    }

    if (format === 'd.m.Y') {
      return `${day}.${month}.${year}`;
    }
  }

  triggerEventRenderHotOffers() {
    const currentSearchService = this.getSearchService();
    const lastSearchParams = currentSearchService.getLastSearchParams();
    const searchFormValues = this.hotelToursPageConfig.searchFormValues as any;
    const departCityId = this.resolveNumberValue(
      lastSearchParams?.departCityId,
      (lastSearchParams as any)?.departCity,
      searchFormValues?.departCityId,
      searchFormValues?.departCity,
    );
    const countryId = this.resolveNumberValue(
      lastSearchParams?.countryId,
      (lastSearchParams as any)?.country,
      searchFormValues?.countryId,
      searchFormValues?.country,
    );

    if (departCityId == null || countryId == null) {
      return;
    }

    const cities = (window as any).cities;
    const citiesArray = Array.isArray(cities) ? cities : Object.values(cities || {});

    // Ищем город по departCityId
    const city = citiesArray.find((item: any) => item.find_id === departCityId);

    if (!city) {
      return;
    }

    // Вызываем событие которое отрисует слайдер горящих (main.js)
    const event = new CustomEvent('render-hot-offers', {
      detail: {
        appendAfterSelector: environment.mobile ? '.default-findtours' : '#angularHottable',
        cityId: city.id,
        countryId
      }
    });

    window.dispatchEvent(event);
  }

  selectDate(event: MouseEvent, date: string): void {
    this.triggerEvent();
    if (this.hotelToursPageConfig.mode === MODE.MODE_SEARCH) {
      event.preventDefault();
      const win = this.windowRef.nativeWindow;
      const last = this.searchService.getLastSearchParams();
      const searchDate = date.includes('.') ? date.split('.').reverse().join('-') : date;
      const params = {
        ...this.hotelToursPageConfig.searchFormValues,
        ...win.lastSearchParams,
        adults: last.adults, childAges: last.childAges,
        country: last.countryId, departCity: last.departCityId,
        hotels: last.hotels, regionIds: last.regions, starsList: last.stars,
        mealIds: last.meals, operatorIds: last.operators, airlineCodes: last.airlines,
        nightsFrom: last.nightsFrom, nightsTo: last.nightsTo,
        dateFrom: searchDate, dateTo: searchDate, delta: 0, forceDates: 1,
        hotelsSearch: last.onlyHotels ? 1 : 0,
      };
      if (last.onlyHotels) {
        const checkout = new Date(searchDate);
        const nights = (new Date(last.dateTo).getTime() - new Date(last.dateFrom).getTime()) / 86400000;
        checkout.setUTCDate(checkout.getUTCDate() + nights);
        params.dateTo = checkout.toISOString().slice(0, 10);
      }
      const url = new URL(win.location.href);
      url.searchParams.set('dateFrom', date);
      url.searchParams.set('dateTo', this.toDottedDate(params.dateTo));
      url.searchParams.set('delta', '0');
      url.searchParams.set('forceDates', '1');
      url.searchParams.set('from', 'alternative_date');
      url.searchParams.set('search_source', 'empty_dates');
      url.searchParams.set('search_parent', this.analyticsContext.search_attempt_id);
      win.history.replaceState(win.history.state, '', url.toString());
      win.dispatchEvent(new CustomEvent('search:start-new', {
        detail: {formParams: params, isOnlyHotels: !!last.onlyHotels, searchTrigger: 'empty_dates'},
      }));
      return;
    }
    if (this.hotelToursPageConfig.mode !== MODE.MODE_TOUR_LIST) return;

    event.preventDefault();
    const searchDate = date.includes('.') ? date.split('.').reverse().join('-') : date;
    const params = {
      ...this.hotelToursSearchService.getLastSearchParams(),
      dateFrom: searchDate,
      dateTo: searchDate,
      forceDates: 1,
    };
    this.eventBusService.shortSearchParams$.next(params);
    this.hotelToursSearchService.startNewSearchByHotelId(params, 'empty_dates');
    this.eventBusService.refreshShortSearchCalendar$.next();
  }

  getLink(date) {
    const location = this.windowRef.nativeWindow.location;
    const path = this.hotelToursPageConfig.mode === MODE.MODE_TOUR_LIST
      ? location.pathname
      : '/new/find-tours';
    const url = new URL(path, location.origin);
    const params = this.buildSearchLinkParams(date);

    url.search = params.toString();

    return url.toString();
  }

  private buildSearchLinkParams(date: string): URLSearchParams {
    const currentSearchService = this.getSearchService();
    const lastSearchParams = currentSearchService.getLastSearchParams() as any;
    const searchFormValues = this.hotelToursPageConfig.searchFormValues as any;
    const cookieSearchParams = this.readLastSearchParamsCookie();
    const params = new URLSearchParams();

    const sourceValues = [
      lastSearchParams,
      searchFormValues,
      cookieSearchParams,
    ];

    this.setFirstParam(params, 'departCity', sourceValues, ['departCityId', 'departCity']);
    this.setFirstParam(params, 'country', sourceValues, ['countryId', 'country']);
    this.setFirstCsvParam(params, 'regions', sourceValues, ['regions', 'regionIds', 'region']);
    this.setFirstCsvParam(params, 'hotels', sourceValues, ['hotels', 'hotel']);
    this.setDaysParams(params, sourceValues);
    this.setFirstCsvParam(params, 'starsList', sourceValues, ['stars', 'starsList']);
    this.setFirstCsvParam(params, 'meals', sourceValues, ['meals', 'mealIds']);
    this.setFirstCsvParam(params, 'operators', sourceValues, ['operators', 'operatorIds']);
    this.setFirstCsvParam(params, 'airlines', sourceValues, ['airlines', 'airlineCodes']);
    this.setFirstParam(params, 'adult', sourceValues, ['adults', 'adult']);
    this.setChildParams(params, sourceValues);
    this.setFirstParam(params, 'delta', sourceValues, ['delta']);
    this.setFirstParam(params, 'hotelsSearch', sourceValues, ['hotelsSearch']);
    this.setFirstParam(params, 'splitRooms', sourceValues, ['splitRooms']);

    params.set('dateFrom', date);
    params.set('dateTo', date);
    params.set('forceDates', '1');
    params.set('search', '1');
    params.set('search_source', 'empty_dates');
    params.set('from', 'alternative_date');
    const context = this.analyticsContext;
    if (context) { params.set('search_parent', context.search_attempt_id); }

    return params;
  }

  private setDaysParams(params: URLSearchParams, sourceValues: any[]): void {
    const daysFrom = this.resolveNumberValueFromSources(sourceValues, ['daysFrom']);
    const daysTo = this.resolveNumberValueFromSources(sourceValues, ['daysTo']);
    const nightsFrom = this.resolveNumberValueFromSources(sourceValues, ['nightsFrom']);
    const nightsTo = this.resolveNumberValueFromSources(sourceValues, ['nightsTo']);

    if (daysFrom != null) {
      params.set('daysFrom', String(daysFrom));
    } else if (nightsFrom != null) {
      params.set('daysFrom', String(nightsFrom + 1));
    }

    if (daysTo != null) {
      params.set('daysTo', String(daysTo));
    } else if (nightsTo != null) {
      params.set('daysTo', String(nightsTo + 1));
    }
  }

  private setChildParams(params: URLSearchParams, sourceValues: any[]): void {
    const childAges = this.resolveValueFromSources(sourceValues, ['childAges']);
    const childAgesValue = this.normalizeCsvValue(childAges);
    const childCount = this.resolveNumberValueFromSources(sourceValues, ['child', 'children']);

    if (childAgesValue) {
      params.set('childAges', childAgesValue);
      params.set('child', String(childAgesValue.split(',').filter((value) => value !== '').length));
      return;
    }

    if (childCount != null) {
      params.set('child', String(childCount));
    }
  }

  private setFirstParam(params: URLSearchParams, targetKey: string, sourceValues: any[], sourceKeys: string[]): void {
    const value = this.resolveValueFromSources(sourceValues, sourceKeys);
    const normalizedValue = this.normalizeScalarValue(value);

    if (normalizedValue !== '') {
      params.set(targetKey, normalizedValue);
    }
  }

  private setFirstCsvParam(params: URLSearchParams, targetKey: string, sourceValues: any[], sourceKeys: string[]): void {
    const value = this.resolveValueFromSources(sourceValues, sourceKeys);
    const normalizedValue = this.normalizeCsvValue(value);

    if (normalizedValue !== '') {
      params.set(targetKey, normalizedValue);
    }
  }

  private resolveNumberValueFromSources(sourceValues: any[], sourceKeys: string[]): number | null {
    const value = this.resolveValueFromSources(sourceValues, sourceKeys);

    return this.resolveNumberValue(value);
  }

  private resolveValueFromSources(sourceValues: any[], sourceKeys: string[]): unknown {
    for (const source of sourceValues) {
      if (!source || typeof source !== 'object') {
        continue;
      }

      for (const key of sourceKeys) {
        if (!Object.prototype.hasOwnProperty.call(source, key)) {
          continue;
        }

        const value = source[key];
        if (this.hasSearchLinkValue(value)) {
          return value;
        }
      }
    }

    return null;
  }

  private hasSearchLinkValue(value: unknown): boolean {
    if (Array.isArray(value)) {
      return value.length > 0;
    }

    if (value === null || typeof value === 'undefined') {
      return false;
    }

    return String(value).trim() !== '';
  }

  private normalizeScalarValue(value: unknown): string {
    if (Array.isArray(value)) {
      return this.normalizeCsvValue(value);
    }

    if (typeof value === 'boolean') {
      return value ? '1' : '0';
    }

    if (value === null || typeof value === 'undefined') {
      return '';
    }

    return String(value).trim();
  }

  private normalizeCsvValue(value: unknown): string {
    if (Array.isArray(value)) {
      return value
        .map((item) => this.normalizeScalarValue(item))
        .filter((item) => item !== '')
        .join(',');
    }

    return this.normalizeScalarValue(value);
  }

  private readLastSearchParamsCookie(): Record<string, string> | null {
    const cookieValue = this.readCookie('last-search-params');
    if (!cookieValue) {
      return null;
    }

    const params = new URLSearchParams(cookieValue.charAt(0) === '?' ? cookieValue.slice(1) : cookieValue);
    const values: Record<string, string> = {};

    params.forEach((value, key) => {
      values[key] = value;
    });

    return Object.keys(values).length ? values : null;
  }

  private readCookie(name: string): string {
    const cookie = this.windowRef.nativeWindow.document.cookie || '';
    if (!cookie) {
      return '';
    }

    const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const match = cookie.match(new RegExp(`(?:^|; )${escapedName}=([^;]*)`));

    if (!match) {
      return '';
    }

    try {
      return decodeURIComponent(match[1]);
    } catch (error) {
      return match[1];
    }
  }

  private get analyticsPlace(): string {
    return this.hotelToursPageConfig.mode === MODE.MODE_TOUR_LIST ? 'hotel-tour-list' : 'clientSearch';
  }

  triggerEvent() {
    this.windowRef.nativeWindow.brxTracking.sendAnalyticsEvent(this.analyticsContext?.search_scope === 'general' ? 'main_search_click_on_dates_when_search_results_empty' : 'hotel_search_click_on_dates_when_search_results_empty', {...this.analyticsContext, place: this.analyticsPlace});
  }

  get isHotelTourList(): boolean {
    return this.hotelToursPageConfig.mode === MODE.MODE_TOUR_LIST;
  }

  get canOpenCalendar(): boolean {
    const params = this.hotelToursSearchService.getLastSearchParams();
    if (!environment.mobile && this.isHotelTourList && !params?.hotelsSearch) {
      return false;
    }
    return !params?.splitRooms || !!params?.hotelsSearch;
  }

  get emptyCalendarLabel(): string {
    return this.hotelToursSearchService.getLastSearchParams()?.hotelsSearch
      ? this.windowRef.nativeWindow.app.translations.hotelStayCalendar.title
      : this.mobileMessages.openPriceCalendar;
  }

  openPriceCalendar(event?: Event): void {
    event?.stopPropagation();
    this.eventBusService.openCalendarFromEmptyResult$.next();
  }

  get hasAvailableDates() {
    return this.datesArray.length > 0;
  }

  get desktopAvailableDates() {
    return this.datesArray.flat().slice(0, 8);
  }

  get desktopHintText() {
    if (this.isHotelTourList) return this.mobileMessages.titleDescription;
    if (!this.loading && !this.hasAvailableDates) {
      return this.mobileMessages.noAvailableDates;
    }

    return this.hasAvailableDates
      ? `${this.mobileMessages.titleDescription} или выберите ближайшую доступную дату`
      : this.mobileMessages.titleDescription;
  }

  get mobileHintText() {
    if (this.isHotelTourList) return this.mobileMessages.titleDescription;
    if (!this.loading && !this.hasAvailableDates) {
      return this.mobileMessages.noAvailableDates;
    }

    return this.hasAvailableDates
      ? `${this.mobileMessages.titleDescription} <br> или выберите ближайшую доступную дату`
      : this.mobileMessages.titleDescription;
  }

  private initSwiperIfNeeded() {
    if (!environment.mobile || this.loading || !this.datesArray.length || !this.swiperContainer?.nativeElement) {
      return;
    }

    if (this.swiperInstance) {
      this.swiperInstance.update();
      return;
    }

    this.swiperInstance = new Swiper(this.swiperContainer.nativeElement, {
      ...this.swiperConfig,
      modules: [Pagination, Navigation],
    });
  }

  private destroySwiper() {
    if (!this.swiperInstance) {
      return;
    }

    this.swiperInstance.destroy(true, true);
    this.swiperInstance = null;
  }

  private startLoadingTimeout() {
    this.clearLoadingTimeout();
    this.loadingTimeoutId = setTimeout(() => {
      if (!this.loading) {
        return;
      }

      this.showEmptyStateContent = true;
      this.cdRef.detectChanges();
    }, ToursNotFoundComponent.LOADING_TIMEOUT_MS);
  }

  private clearLoadingTimeout() {
    if (!this.loadingTimeoutId) {
      return;
    }

    clearTimeout(this.loadingTimeoutId);
    this.loadingTimeoutId = null;
  }

  private finishLoading() {
    this.clearLoadingTimeout();
    this.loading = false;
    this.showEmptyStateContent = true;
    this.cdRef.detectChanges();
    this.initSwiperIfNeeded();
  }

  private pickNearestAvailableDates(availableDates: string[], dateFrom: string, dateTo: string): string[] {
    if (!Array.isArray(availableDates) || availableDates.length === 0) {
      return [];
    }

    const fromTime = this.parseDate(dateFrom)?.getTime();
    const toTime = this.parseDate(dateTo)?.getTime() ?? fromTime;

    if (typeof fromTime !== 'number' || typeof toTime !== 'number') {
      return [];
    }

    const selectedDate = this.parseDate(dateFrom)!;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const minTime = Math.max(today.getTime(), this.shiftCalendarMonths(selectedDate, -2).getTime());
    const maxTime = this.shiftCalendarMonths(selectedDate, 2).getTime();

    return [...availableDates]
      .filter((date) => {
        const parsedDate = this.parseDate(date);

        if (!parsedDate) {
          return false;
        }

        const currentTime = parsedDate.getTime();
        return currentTime >= minTime && currentTime <= maxTime
          && (currentTime < fromTime || currentTime > toTime);
      })
      .sort((left, right) => {
        const leftTime = this.parseDate(left)?.getTime() ?? 0;
        const rightTime = this.parseDate(right)?.getTime() ?? 0;
        const leftDiff = this.getDistanceFromRange(leftTime, fromTime, toTime);
        const rightDiff = this.getDistanceFromRange(rightTime, fromTime, toTime);

        return leftDiff - rightDiff;
      })
      .slice(0, 6)
      .sort((left, right) => {
        const leftTime = this.parseDate(left)?.getTime() ?? 0;
        const rightTime = this.parseDate(right)?.getTime() ?? 0;
        return leftTime - rightTime;
      });
  }

  private shiftCalendarMonths(date: Date, months: number): Date {
    const result = new Date(date.getFullYear(), date.getMonth() + months, 1);
    const lastDay = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();
    result.setDate(Math.min(date.getDate(), lastDay));
    return result;
  }

  private getDistanceFromRange(value: number, fromTime: number, toTime: number): number {
    if (value < fromTime) {
      return fromTime - value;
    }

    if (value > toTime) {
      return value - toTime;
    }

    return 0;
  }

  private parseDate(value: string): Date | null {
    if (!value) {
      return null;
    }

    const normalizedValue = value.includes('.') ? value.split('.').reverse().join('-') : value.slice(0, 10);
    const date = new Date(normalizedValue);

    if (Number.isNaN(date.getTime())) {
      return null;
    }

    date.setHours(0, 0, 0, 0);
    return date;
  }

  private toDottedDate(value: string): string {
    const date = this.parseDate(value);

    if (!date) {
      return value;
    }

    return this.formatDate(date, 'd.m.Y');
  }

  private resolveDateValue(...values: unknown[]): string | null {
    for (const value of values) {
      if (value instanceof Date && !Number.isNaN(value.getTime())) {
        return this.formatDate(value);
      }

      if (typeof value !== 'string' || !value.trim()) {
        continue;
      }

      const parsedDate = this.parseDate(value);
      if (parsedDate) {
        return this.formatDate(parsedDate);
      }
    }

    return null;
  }

  private resolveNumberValue(...values: unknown[]): number | null {
    for (const value of values) {
      if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
      }

      if (typeof value === 'string' && value.trim() !== '') {
        const parsedValue = Number(value);

        if (Number.isFinite(parsedValue)) {
          return parsedValue;
        }
      }
    }

    return null;
  }
}
